import { useEffect, useMemo, useRef, useState } from 'react'
import styles from './hub.module.css'
import { loadHubApps } from '../../api/hubApi'
import BridgerIcon from '../../assets/bridger-hub-icon.svg'

/**
 * Bridger Application Hub – public landing page (no authentication).
 * Application cards are driven by src/data/app-directory.csv (the App Directory sheet).
 * Layout, copy, filters and search behaviour follow NSA_Application_Hub_Cards.html.
 */

const JSM = 'https://bridgerins.atlassian.net/servicedesk/customer/portals'
const ORDER = [
  'Policy & Underwriting', 'Claims Admin', 'Analytics & Insights', 'Project Management',
  'Support & Service Desk', 'Knowledge Base', 'Quality & Operations', 'Call Center',
  'Engineering & Technology',
]
const ENV_ORDER = ['prod', 'uat', 'dev']
const ENV_NAME  = { prod: 'PROD', uat: 'UAT', dev: 'DEV' }
const ENVS      = ['prod', 'uat', 'dev', 'all']

/* ── Search (same word-based scoring as the prototype) ─────────────────────── */
const STOP = new Set('a an and are can do does for find from get go how i in is it me my of on or the to want where which who with what need please something anything'.split(' '))
function stem(w) {
  if (w.length > 4 && w.endsWith('ies')) return w.slice(0, -3) + 'y'
  if (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) return w.slice(0, -1)
  return w
}
const words      = (t) => String(t || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean)
const queryTerms = (q) => words(q).filter((w) => !STOP.has(w)).map(stem)
const hit        = (list, t) => (t.length >= 4 ? list.some((w) => w.startsWith(t)) : list.includes(t))
function score(ix, terms) {
  let s = 0
  for (const t of terms) {
    const inName = ix.name.some((w) => w.startsWith(t))
    if (!inName && !hit(ix.all, t)) return -1 // every word must match
    s += inName ? 2 : 1
  }
  return s
}
const pinRank = (a) => (Number.isFinite(a.pin) ? a.pin : 1000)

/* ── View state lives in the URL so a filtered view is shareable ───────────── */
function readInitialState() {
  const p = new URLSearchParams(window.location.search)
  return {
    env:     ENVS.includes(p.get('env')) ? p.get('env') : 'prod',
    cat:     p.get('cat') || 'All',
    q:       (p.get('q') || '').slice(0, 80),
    hideDev: p.get('dev') !== 'show',
  }
}

function EnvButton({ app, env, compact }) {
  if (!app[env]) {
    return (
      <span className={`${styles.btn} ${styles.none}`}>
        {compact ? `No ${ENV_NAME[env]}` : `No ${ENV_NAME[env]} link recorded`}
      </span>
    )
  }
  return (
    <a
      className={`${styles.btn} ${styles[env]}`}
      href={app[env]}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open ${app.name} in ${ENV_NAME[env]} (opens in new tab)`}
    >
      {compact ? ENV_NAME[env] : `Open ${ENV_NAME[env]}`}
    </a>
  )
}

function AppCard({ app, env }) {
  const legacy = app.status === 'Legacy' || app.status === 'Retiring'
  return (
    <li>
      <article className={`${styles.card}${legacy ? ` ${styles.legacy}` : ''}`} aria-labelledby={`t-${app.id}`}>
        <div className={styles.cardTop}>
          <div>
            <p className={`${styles.cat} ${styles.caption}`}>{app.cat}</p>
            <h3 className={styles.h3} id={`t-${app.id}`}>{app.name}</h3>
            <p className={`${styles.body2} ${styles.muted}`}>{app.desc || 'Description to be added'}</p>
          </div>
          <span className={`${styles.badge}${legacy ? ` ${styles.legacy}` : ''}`}>{legacy ? app.status : app.aud}</span>
        </div>
        {env === 'all' ? (
          <div className={styles.btns}>
            {ENV_ORDER.map((e) => <EnvButton key={e} app={app} env={e} compact />)}
          </div>
        ) : (
          <EnvButton app={app} env={env} />
        )}
        <div className={`${styles.cardFoot} ${styles.caption}`}>
          <span className={styles.muted}>Owner: {app.owner || 'to be confirmed'}</span>
          <a
            href={app.ticket || JSM}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Report an issue with ${app.name} (opens in new tab)`}
          >
            Report an issue
          </a>
        </div>
      </article>
    </li>
  )
}

export default function HubPage() {
  const [{ apps, error }] = useState(() => {
    try {
      return { apps: loadHubApps(), error: false }
    } catch (err) {
      console.error(err)
      return { apps: [], error: true }
    }
  })
  const [view, setView] = useState(readInitialState)
  const searchRef = useRef(null)

  useEffect(() => {
    const prev = document.title
    document.title = 'Bridger Application Hub'
    return () => { document.title = prev }
  }, [])

  const index = useMemo(() => new Map((apps || []).map((a) => [a, {
    name: words(a.name).map(stem),
    all:  words([a.name, a.cat, a.desc, a.kw].join(' ')).map(stem),
  }])), [apps])

  // Fixed prototype order; any new category added in the sheet is appended at the end.
  const order = useMemo(() => {
    const extra = [...new Set((apps || []).map((a) => a.cat).filter((c) => !ORDER.includes(c)))]
    return [...ORDER, ...extra]
  }, [apps])

  const cats = useMemo(
    () => order.filter((c) => (apps || []).some((a) => a.cat === c && (!view.hideDev || a.aud !== 'Technology'))),
    [apps, order, view.hideDev],
  )
  const cat = view.cat !== 'All' && !cats.includes(view.cat) ? 'All' : view.cat

  useEffect(() => {
    if (!apps) return
    const p = new URLSearchParams()
    if (view.env !== 'prod') p.set('env', view.env)
    if (cat !== 'All') p.set('cat', cat)
    if (view.q) p.set('q', view.q)
    if (!view.hideDev) p.set('dev', 'show')
    const qs = p.toString()
    try { window.history.replaceState(window.history.state, '', qs ? `?${qs}` : window.location.pathname) } catch { /* non-fatal */ }
  }, [apps, view, cat])

  const set = (patch) => setView((v) => ({ ...v, ...patch }))
  const clearFilters = () => { set({ q: '', cat: 'All' }); searchRef.current?.focus() }

  const terms = queryTerms(view.q.trim().toLowerCase())
  const shown = useMemo(() => {
    if (!apps) return []
    return apps
      .filter((a) => (!view.hideDev || a.aud !== 'Technology') && (cat === 'All' || a.cat === cat))
      .map((a) => ({ a, s: terms.length ? score(index.get(a), terms) : 0 }))
      .filter((x) => x.s >= 0)
      .sort((x, y) => (y.s - x.s) || (pinRank(x.a) - pinRank(y.a)) || (order.indexOf(x.a.cat) - order.indexOf(y.a.cat)))
      .map((x) => x.a)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apps, index, order, view.hideDev, cat, view.q])

  let count = ''
  let content

  if (error) {
    count = 'The application list could not be displayed.'
    content = (
      <div className={`${styles.state} ${styles.error}`} role="alert">
        <p className={styles.h3}>The application list could not be displayed</p>
        <p className={`${styles.body2} ${styles.muted}`}>Reload the page. If it happens again, tell the directory owner and use the App Directory sheet in the meantime.</p>
        <button type="button" onClick={() => window.location.reload()}>Reload page</button>
      </div>
    )
  } else if (!apps.length) {
    content = (
      <div className={styles.state}>
        <p className={styles.h3}>No applications have been added yet</p>
        <p className={`${styles.body2} ${styles.muted}`}>Add applications to src/data/app-directory.csv, then rebuild and redeploy.</p>
      </div>
    )
  } else if (!shown.length) {
    const hiddenDev = view.hideDev && terms.length
      ? apps.filter((a) => a.aud === 'Technology' && (cat === 'All' || a.cat === cat) && score(index.get(a), terms) >= 0)
      : []
    if (hiddenDev.length) {
      const names = hiddenDev.map((a) => a.name).join(', ')
      const many = hiddenDev.length > 1
      count = `No business apps match. ${hiddenDev.length} developer tool${many ? 's match' : ' matches'}.`
      content = (
        <div className={styles.state}>
          <p className={styles.h3}>{names} {many ? 'are developer tools' : 'is a developer tool'}</p>
          <p className={`${styles.body2} ${styles.muted}`}>Developer tools are hidden. Show them to see {many ? 'these apps' : 'this app'}.</p>
          <button type="button" onClick={() => { set({ hideDev: false }); searchRef.current?.focus() }}>Show developer tools</button>
        </div>
      )
    } else {
      count = 'No applications match these filters.'
      content = (
        <div className={styles.state}>
          <p className={styles.h3}>No applications match these filters</p>
          <p className={`${styles.body2} ${styles.muted}`}>
            {view.q.trim() ? `Nothing matches “${view.q}”. Try fewer or different words` : 'Nothing in this category'}
            {view.hideDev ? ' among business-facing apps' : ''}.
          </p>
          <button type="button" onClick={clearFilters}>Clear filters</button>
        </div>
      )
    }
  } else {
    const withLink = view.env === 'all' ? null : shown.filter((a) => a[view.env]).length
    count = `${shown.length} application${shown.length > 1 ? 's' : ''}` +
      (withLink === null ? ', showing all environment links' : `, ${withLink} with a ${ENV_NAME[view.env]} link`)
    content = (
      <ul className={styles.grid}>
        {shown.map((a) => <AppCard key={a.id} app={a} env={view.env} />)}
      </ul>
    )
  }

  return (
    <div className={styles.root}>
      <a className={styles.skip} href="#apps">Skip to applications</a>
      <div className={styles.wrap}>
        <header>
          <div className={styles.brand}>
            <img className={styles.logo} src={BridgerIcon} alt="" />
            <div className={styles.brandText}>
              <h1 className={`${styles.h1} ${styles.title}`}>Bridger Application Hub</h1>
              <p className={styles.muted}>The right link for each environment, and where to raise a ticket.</p>
            </div>
          </div>
          <span className={`${styles.conf} ${styles.caption} ${styles.muted}`}>Company confidential – internal use only</span>
        </header>

        <form onSubmit={(e) => e.preventDefault()}>
          <fieldset className={styles.env}>
            <legend className={styles.h2}>Environment</legend>
            <div className={styles.envOpts}>
              {[
                { v: 'prod', cls: styles.prod, t: 'PROD – live business',  d: 'Real policies and claims. Never test here.' },
                { v: 'uat',  cls: styles.uat,  t: 'UAT – testing',         d: 'Use this for testing. No real customer data.' },
                { v: 'dev',  cls: styles.dev,  t: 'DEV – development',     d: 'Development team only.' },
                { v: 'all',  cls: styles.all,  t: 'All environments',      d: 'Show PROD, UAT and DEV links together.' },
              ].map((o) => (
                <label key={o.v} className={`${styles.opt} ${o.cls}`}>
                  <input type="radio" name="env" value={o.v} checked={view.env === o.v} onChange={() => set({ env: o.v })} />
                  <span className={styles.t}>{o.t}</span>
                  <span className={`${styles.d} ${styles.body2} ${styles.muted}`}>{o.d}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <section className={styles.help} aria-labelledby="help-h">
            <h2 id="help-h" className={styles.h2}>Something not working? Where to raise it</h2>
            <ul className={styles.body2}>
              <li>Production issue: <a href="https://bridgerins.atlassian.net/servicedesk/customer/portals" target="_blank" rel="noopener noreferrer">JSM Service Desk<span className={styles.srOnly}> (opens in new tab)</span></a></li>
              <li>Access or login request: <a href="https://bridgerins.atlassian.net/servicedesk/customer/portals" target="_blank" rel="noopener noreferrer">JSM Service Desk<span className={styles.srOnly}> (opens in new tab)</span></a></li>
              <li>How-to question: <a href="https://bridgerins.atlassian.net/wiki/spaces/SIN/overview" target="_blank" rel="noopener noreferrer">Confluence<span className={styles.srOnly}> (opens in new tab)</span></a></li>
              <li>UAT defect: <span className={styles.tbc}>to be confirmed</span></li>
              <li>Enhancement or new requirement: <span className={styles.tbc}>to be confirmed</span></li>
              <li>Report or data issue: <span className={styles.tbc}>to be confirmed</span></li>
            </ul>
          </section>

          <div className={styles.filters}>
            <div className={styles.field}>
              <label htmlFor="q" className={styles.body2}>Search applications</label>
              <input
                id="q"
                ref={searchRef}
                type="search"
                autoComplete="off"
                aria-describedby="q-hint"
                placeholder="e.g. raise a ticket, dashboard, policy audit"
                value={view.q}
                onChange={(e) => set({ q: e.target.value })}
              />
              <span id="q-hint" className={`${styles.caption} ${styles.muted}`}>Type what you need in any order – app name, task or keyword.</span>
            </div>
            <label className={`${styles.check} ${styles.body2}`}>
              <input type="checkbox" checked={view.hideDev} onChange={(e) => set({ hideDev: e.target.checked })} /> Hide developer tools
            </label>
          </div>
          <div className={styles.chips} role="group" aria-label="Filter by category">
            {['All', ...cats].map((c) => (
              <button key={c} type="button" className={styles.chip} aria-pressed={c === cat} onClick={() => set({ cat: c })}>{c}</button>
            ))}
          </div>
        </form>

        <main id="apps" tabIndex={-1}>
          <h2 className={styles.srOnly}>Applications</h2>
          <p className={`${styles.count} ${styles.body2} ${styles.muted}`} role="status" aria-live="polite">{count}</p>
          <div>{content}</div>
        </main>

        <footer className={`${styles.caption} ${styles.muted}`}>
          Something wrong or missing on this page? Tell the directory owner. Data source: App Directory sheet, NSA_Application_Hub.xlsx.
        </footer>
      </div>
    </div>
  )
}
